/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  createContext,
  useCallback,
  useState,
  useContext,
  useEffect,
  // useEffect,\
} from "react";
import { toast } from "react-toastify";
import { IUser } from "../@types/system";
import api from "../service/api";
import { useLoading } from "../store/useLoading";
import { useStore } from "../store";

interface AuthState {
  user: IUser;
  token: string;
}

interface SignInCredentials {
  email: string;
  password: string;
}

interface SignInResult {
  ok: boolean;
  mustChangePassword?: boolean;
}

interface AuthContext {
  user: IUser;
  signIn(credentials: SignInCredentials): Promise<SignInResult>;
  signOut(): void;
}

const AuthContext = createContext<AuthContext>({} as AuthContext);

const AuthProvider = ({ children }: any) => {
  const setLoading = useLoading((state) => state.setLoading);
  const setUser = useStore((state) => state.setUser);
  const user = useStore((state) => state.user);
  const setToken = useStore((state) => state.setToken);
  const clearLogin = useStore((state) => state.clearLogin);

  const [data, setData] = useState<AuthState>(() => {
    const tokenLoc = localStorage.getItem("@apcap:token");
    const userLoc = localStorage.getItem("@apcap:user");

    if (tokenLoc && userLoc) {
      api.defaults.headers.common["Authorization"] = `Bearer ${tokenLoc}`;
      setToken(tokenLoc);
      setUser(JSON.parse(userLoc));

      return { token: tokenLoc, user: JSON.parse(userLoc) };
    }

    return {} as AuthState;
  });

  const signIn = useCallback(async ({
    email,
    password,
  }: SignInCredentials): Promise<SignInResult> => {
    setLoading(true);

    if (email === "" || password === "") {
      toast.error("Todos os campos são obrigatorios", {
        containerId: "toast",
      });
      setLoading(false);
      return { ok: false };
    }

    try {
      const response = await api.post("/sessions", {
        email,
        password,
      });

      const { token, user } = response.data;

      if (user.must_change_password) {
        sessionStorage.setItem("@apcap:provisionalPwd", password);
      }

      setUser(user);
      setToken(token);
      localStorage.setItem("@apcap:token", token);
      localStorage.setItem("@apcap:user", JSON.stringify(user));

      api.defaults.headers.common["Authorization"] = `Bearer ${token}`;

      setData({ token, user });
      setLoading(false);
      return {
        ok: true,
        mustChangePassword: !!user.must_change_password,
      };
    } catch (e: any) {
      setLoading(false);

      const status = e.response?.status;
      const apiMessage =
        e.response?.data?.message ||
        e.response?.data?.error ||
        (typeof e.response?.data === "string" ? e.response.data : null);

      if (status === 400 || status === 401) {
        toast.error(
          typeof apiMessage === "string"
            ? apiMessage
            : "E-mail ou senha não conferem",
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
            theme: "colored",
          }
        );
        return { ok: false };
      }

      toast.error(
        e.code === "ERR_NETWORK"
          ? "Não foi possível conectar à API. Verifique se o servidor está no ar e a URL em service/api.ts."
          : typeof apiMessage === "string"
            ? apiMessage
            : "Erro ao entrar. Tente novamente.",
        { position: "top-right", theme: "colored" }
      );
      return { ok: false };
    }
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem("@apcap:token");
    localStorage.removeItem("@apcap:user");
    sessionStorage.removeItem("@apcap:provisionalPwd");

    clearLogin();

    setData({} as AuthState);
  }, []);

  const verifyToken = useCallback(async () => {
    if (user.id) {
      api
        .get(`/user/${user.id}`)
        .then((res) => {
          if (res.status !== 200) {
            signOut();
          }
        })
        .catch((error) => {
          console.log("ERROR", error);
          signOut();
        });
    } else {
      signOut();
    }
  }, [user, signOut]);

  useEffect(() => {
    verifyToken();
  }, []);

  return (
    <AuthContext.Provider value={{ user: data.user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): AuthContext {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

// eslint-disable-next-line react-refresh/only-export-components
export { AuthProvider, useAuth };
